/**
 * User menu — avatar trigger with links to your profile and your export record,
 * plus sign-out. Settings intentionally live in the org/tenant menu, not here
 * (this stays personal: profile + session + what you took out of the product).
 *
 * "Your exports" is here because the export LEDGER — a compliance audit trail of
 * who exported what — had exactly one way in: a tab inside Settings. The
 * `ExportMenu` that writes a row never pointed at it, and that menu ships from
 * `@authe/export`, so the entry point it needs cannot be added from the app.
 * This is the second door, and the one that is personal: anyone who can export
 * can read their own record of having done so.
 *
 * "Getting Started" is here for the opposite reason: it is read once, at the
 * beginning, and then never again — so a permanent slot on the sidebar rail was
 * the wrong price for it. It stays offered by the landing page and by `?guide=1`,
 * it is still findable by name in the command palette, and this menu is where the
 * things about YOU rather than about the data already live.
 */
import { Link } from "@tanstack/react-router";
import { ChevronDown, Compass, FileDown, LogOut, User } from "lucide-react";
import { useSession } from "@authe/ui-core/session";
import {
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@authe/ui-core";
import { Hint } from "./Hint";
import { ROUTES } from "@/config";
import { logoutDestination } from "@/app/logout-destination";

export function ProfileMenu(): React.ReactElement {
  const { session, logout } = useSession();
  const user = session?.user;
  const accountLabel = `Account menu: ${user?.name ?? user?.email ?? "Your account"}`;

  return (
    <DropdownMenu>
      <Hint label={accountLabel}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex items-center gap-2 px-2" aria-label={accountLabel}>
            <Avatar
              size="sm"
              src={user?.avatar ?? undefined}
              alt={user?.name ?? "User"}
              fallback={(user?.name ?? user?.email ?? "U").slice(0, 1).toUpperCase()}
            />
            <span className="hidden max-w-[10rem] truncate text-sm md:inline">
              {user?.name ?? user?.email ?? "Account"}
            </span>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
      </Hint>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="truncate">{user?.name ?? "Signed in"}</div>
          <div className="truncate text-xs font-normal text-muted-foreground">{user?.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={ROUTES.profile}>
            <User className="h-4 w-4" />
            Your profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={ROUTES.settingsExports}>
            <FileDown className="h-4 w-4" />
            Your exports
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={ROUTES.gettingStarted}>
            <Compass className="h-4 w-4" />
            Getting Started
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() =>
            // Land on the login SPA's signed-out view (its own port locally),
            // served outside `RequireSession`, so it
            // won't auto re-login). We deliberately do NOT pass `federated`:
            // that would also end the user's upstream Google/IdP session
            // (Gmail, etc.), which is more than a dashboard sign-out should do.
            // Silent re-login is already prevented by the signed-out view (no
            // auto-login) + the login SPA's `prompt=login` (interactive re-auth).
            void logout({
              returnTo: logoutDestination(),
            })
          }
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
